import type { GridProps } from '@chakra-ui/react'
import { Flex, Grid } from '@chakra-ui/react'
import { useWindowWidth } from '@react-hook/window-size'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { usePrintContext } from '@/context/PrintContext'
import { useStepsContext } from '@/context/StepsContext'

import { useSidebar } from '../../../context/SidebarContext'
import { PageSectionItem } from './PageSectionItem'
import { PageSectionMenu } from './PageSectionMenu'

export interface PageSection {
  id: string | number
  index?: number
  title?: string
  sectionTitle?: string | React.ReactNode
  badge?: number | string
  isDisabled?: boolean
  subsections?: PageSection[]
  isHidden?: boolean

  children?: React.ReactNode
  useFormSection?: boolean
  wide?: boolean
}

export type SidebarProps = GridProps

function getOffsetTop(element: HTMLElement | null) {
  let offset = 0

  while (element) {
    offset += element.offsetTop
    element = element.offsetParent as HTMLElement
  }

  return offset
}

export const useScrollToPageSection = () => {
  // const { sidebarTopOffset } = useSidebar()
  const { currentStepId } = useStepsContext()

  const scrollFunction = useCallback((elementId: string) => {
    const el = document.getElementById(elementId)
    const elOffsetTop = getOffsetTop(el)

    const top = (currentStepId ?? 0) > 1
      ? elOffsetTop - 123  // ostatni kroky maji header, ktery se pri scrollu az zmensi. Proto to tu davam natvrdo 
      : elOffsetTop

    window.scrollTo({
      top: top - 32,
      behavior: 'smooth',
    })
  }, [currentStepId])
  // }, [sidebarTopOffset])

  return scrollFunction
}

export const Sidebar = ({ children }: SidebarProps) => {
  const {
    pageSections,
    // Current waypoint
    currentWaypoint,
    setCurrentWaypoint,
    // top offset
    sidebarTopOffset,
    // menu height
    sidebarMenuHeight,
    setSidebarMenuHeight,
    // sidebar wrapper margin top
    wrapperMarginTop,
  } = useSidebar()

  const menuRef = useRef<HTMLDivElement>(null)
  const sidebarGap = 8
  const windowWidth = useWindowWidth()
  const [currentMenuHeight, setCurrentMenuHeight] = useState(sidebarMenuHeight)
  const [currentMenuTop, setCurrentMenuTop] = useState(0)

  useEffect(() => {
    const menuHeight = menuRef?.current?.getBoundingClientRect().height
    setCurrentMenuTop(menuRef?.current?.getBoundingClientRect().top ?? 0)
    setCurrentMenuHeight(menuHeight)
    if (menuHeight)
      setSidebarMenuHeight?.(menuHeight)

  }, [setSidebarMenuHeight, pageSections])

  const { printMode } = usePrintContext()

  return (
    <Flex
      as="aside"
      sx={{
        alignContent: 'start',
        flexDirection: 'column',
        gap: sidebarGap,
        pt: { md: 12 },
      }}
    >
      {!printMode && <PageSectionMenu
        style={{
          top: sidebarTopOffset ? sidebarTopOffset + 32 : 32,
        }}
        ref={menuRef}
      >
        {pageSections.filter(x => !x.isHidden).map((section, index) => {
          const isCurrent = currentWaypoint == section.id
          const title = section.title ?? section.sectionTitle
          const text = section.index ? `${section.index}. ${title}` : title

          if (title === '') return null

          return (
            <PageSectionItem
              href={`#${section.id}`}
              key={index}
              badge={section.badge}
              isCurrent={isCurrent}
              isDisabled={section.isDisabled}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault()

                if (!section.isDisabled) {
                  setCurrentWaypoint?.(section.id)
                }
              }}
              subsections={section.subsections || undefined}
              zIndex={3}
            >
              {text}
            </PageSectionItem>
          )
        })}
      </PageSectionMenu>
      }

      <Grid
        sx={{ position: { md: 'sticky' } }}
        style={{
          marginTop: windowWidth > 768 && wrapperMarginTop! > currentMenuTop && currentMenuTop != 0 ? wrapperMarginTop! - currentMenuTop : '0',
          top: printMode
            ? (sidebarTopOffset ? sidebarTopOffset + 32 : 32)
            : (sidebarTopOffset &&
              currentMenuHeight &&
              `${sidebarTopOffset + currentMenuHeight + sidebarGap * 8}px`)
          ,
        }}
      >
        {children}
      </Grid>
    </Flex>
  )
}
